import { useState } from 'react';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { grantAiConsent } from '../services/aiConsent';
import { OverlayHeader } from './red/OverlayHeader';

// DSGVO-Einwilligung vor dem ersten KI-Upload.
//
// Props:
//  - uid:       aktuelle User-Id
//  - onAccept:  () => void — nach erfolgreicher Einwilligung
//  - onCancel:  () => void — User bricht ab
export default function AiConsentModal({ uid, onAccept, onCancel }) {
  const { T } = useTheme();
  const [saving, setSaving] = useState(false);

  async function handleAccept() {
    if (!uid) return;
    setSaving(true);
    const ok = await grantAiConsent(uid);
    setSaving(false);
    if (ok) onAccept?.();
  }

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          background: T.surface,
          border: `1px solid ${T.line || T.border}`,
          boxShadow: T.cardShadowLg,
          borderRadius: 18,
          padding: '22px 22px 18px',
          fontFamily: F.family,
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <OverlayHeader
            title="KI-Analyse aktivieren"
            sub={<>Für die automatische Erkennung deiner Dateien wird der Inhalt an <strong style={{ color: T.text }}>Google Gemini</strong> übertragen (Drittland-Übermittlung auf Basis EU-Standardvertragsklauseln). Du musst dem einmal zustimmen, bevor wir starten.</>}
          />
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${T.line || T.border}`,
          borderRadius: R.md,
          padding: '12px 14px',
          marginBottom: 16,
        }}>
          <Point T={T} title="Was übertragen wird">
            Ausschließlich der Dateiinhalt, den du auswählst (CSV, Excel, PDF oder Screenshot).
            Prüfe vor dem Upload, ob deine Datei personenbezogene Felder wie Account-IDs oder
            Wallet-Adressen enthält — diese würden mitübertragen.
          </Point>
          <Point T={T} title="Was nicht passiert">
            Der Inhalt wird laut Google-API-Terms <em>nicht</em> zum Training der
            Modelle verwendet und nicht für andere User sichtbar.
          </Point>
          <Point T={T} title="Wie lange wir speichern">
            Erkannte Transaktionen landen in deinem privaten Bereich. Importprotokolle
            werden nach 24 Monaten automatisch gelöscht.
          </Point>
          <Point T={T} title="Dein Widerruf" last>
            Du kannst die Einwilligung jederzeit in den Einstellungen zurückziehen —
            ab dann steht nur noch der lokale CSV-Parser für bekannte Börsen zur Verfügung.
          </Point>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              flex: 1, padding: '12px',
              background: 'none',
              border: `1px solid ${T.line || T.border}`,
              borderRadius: R.md,
              color: T.sub, fontFamily: F.family,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleAccept}
            disabled={saving || !uid}
            style={{
              flex: 1.4, padding: '12px',
              background: T.redGradient,
              border: 'none', borderRadius: R.md,
              color: '#fff', fontFamily: F.family,
              fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
              boxShadow: T.redGlowSoft,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Speichert…' : 'Einwilligen & fortfahren'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Point({ T, title, children, last = false }) {
  return (
    <div style={{ marginBottom: last ? 0 : 10 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.text,
        letterSpacing: '0.02em', marginBottom: 2,
      }}>
        {title}
      </div>
      <div style={{ fontSize: 11.5, color: T.sub, lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  );
}
